import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  ComponentFactoryResolver,
  ComponentRef,
  EmbeddedViewRef,
  EventEmitter,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  Optional,
  Provider,
  RendererFactory2,
  Type,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CxModalInstance } from './cx-modal-instance.service';

export interface CxModalContext {
  inputs?: { [key: string]: any };
  outputs?: { [key: string]: () => void };
}

export interface CxModalRegisterToken {
  name: string;
  modal: Type<any>;
}
export const CX_MODAL_REGISTER_TOKEN = new InjectionToken<CxModalRegisterToken>('CX_MODAL_REGISTER_TOKEN');
// @TODO This will works in newer Angular with `makeEnvironmentProviders`;
export function provideCxModalRegister(cxModalValues: CxModalRegisterToken[]) {
  return cxModalValues.map(
    (value) =>
      ({
        provide: CX_MODAL_REGISTER_TOKEN,
        useValue: value,
        multi: true,
      }) as Provider,
  );
}

@Injectable({
  providedIn: 'root',
})
export class CxModalService {
  constructor(
    @Inject(DOCUMENT) private document,
    private cfr: ComponentFactoryResolver,
    private rendererFactory: RendererFactory2,
    private injector: Injector,
    private appRef: ApplicationRef,
    @Optional() @Inject(CX_MODAL_REGISTER_TOKEN) private modalRegister: CxModalRegisterToken[],
  ) {}

  private rdr2 = this.rendererFactory.createRenderer(null, null);
  private host = this.document.querySelector('body');

  public createModalFromRegister(instanceId: string, context: CxModalContext = {}) {
    if (this.modalRegister) {
      const modalRef = this.modalRegister.find(({ name }) => name === instanceId);
      if (modalRef) {
        this.createModal(modalRef.modal, context);
      } else {
        console.warn(`Modal with name: ${instanceId} doesn't exists in register`);
      }
    }
  }

  public createModal(instance: Type<unknown>, context: CxModalContext = {}) {
    const componentRefDestroyed$ = new Subject<boolean>();

    const componentRef = this.cfr.resolveComponentFactory(instance).create(this.injector);
    this.appRef.attachView(componentRef.hostView);

    const $modal = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

    this.injectCxModalInstance(componentRef);

    if (context) {
      Object.entries(context['inputs'] || {}).forEach(([key, value]) => {
        componentRef.instance[key] = value;
      });

      Object.entries(context['outputs'] || {}).forEach(([key, value]) => {
        componentRef.instance[key] = new EventEmitter();
        componentRef.instance[key].pipe(takeUntil(componentRefDestroyed$)).subscribe((props) => {
          value.call(props);
        });
      });
    }

    this.rdr2.appendChild(this.host, $modal);
    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  public removeModal(componentRef: any) {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
  }

  private injectCxModalInstance(componentRef: ComponentRef<any>) {
    const componentInstanceRef = componentRef.injector.get(CxModalInstance);

    if (componentInstanceRef) {
      componentInstanceRef.cxInstance = componentRef;
      componentInstanceRef.cxRemoveInstance = () => {
        this.removeModal(componentRef);
      };
    }
  }
}
